import React, { useEffect } from "react";
import Navbar from "../Navbar";
import Footer from "../Footer";
import ContactSection from "../ContactSection";
import Lenis from "@studio-freight/lenis";
import {
  Package,
  Layers,
  TrendingUp,
  FileSpreadsheet,
  RefreshCw,
  Barcode,
  ArrowRight
} from "lucide-react";
import "../styles/website.css";
import "../styles/ProductPages.css";

const InventoryManagement = () => {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      smoothWheel: true,
      smoothTouch: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  const features = [
    {
      title: "Real-Time Stock Tracking",
      description: "Keep tabs on raw materials, works-in-progress, and finished goods across multiple outlets.",
      icon: <Package size={22} />
    },
    {
      title: "Automated Reordering",
      description: "Set minimum threshold levels to trigger low-stock alerts and auto-create purchase requisitions.",
      icon: <RefreshCw size={22} />
    },
    {
      title: "Barcoding & Serialization",
      description: "Generate custom serial numbers and barcodes to scan items during packing and dispatch.",
      icon: <Barcode size={22} />
    },
    {
      title: "Multi-Location Sync",
      description: "Coordinate stock levels across retail outlets, warehouses, and distribution centers.",
      icon: <Layers size={22} />
    },
    {
      title: "Stock Audit & Adjustments",
      description: "Conduct stocktakes with mobile scanners and log shrinkage or adjustments automatically.",
      icon: <FileSpreadsheet size={22} />
    },
    {
      title: "Inventory Valuation",
      description: "Calculate cost of goods sold (COGS) dynamically using FIFO, LIFO, or Weighted Average methods.",
      icon: <TrendingUp size={22} />
    }
  ];

  return (
    <div className="prod-page-wrapper">
      <Navbar />

      {/* HERO */}
      <section className="prod-hero-section">
        <div className="prod-hero-glow"></div>
        <div className="prod-hero-left">
          <span className="prod-badge">INVENTORY MANAGEMENT</span>
          <h1 className="prod-main-title">
            Real-Time Stock. <br />
            <span>Optimal Levels.</span>
          </h1>
          <p className="prod-desc">
            Monitor product logs, establish automated reordering thresholds, synchronize multiple store locations,
            and audit stock levels accurately.
          </p>
          <div className="prod-cta-group">
            <a href="#contact" className="prod-primary-btn">
              Request Demo <ArrowRight size={16} />
            </a>
          </div>
        </div>

        {/* HERO VISUAL MOCKUP */}
        <div className="prod-hero-right">
          <div className="prod-mockup-frame">
            <div className="mockup-header">
              <div className="mockup-dots">
                <div className="mockup-dot red"></div>
                <div className="mockup-dot yellow"></div>
                <div className="mockup-dot green"></div>
              </div>
              <span className="mockup-title">inventory.gtelinq.com/dashboard</span>
            </div>
            <div className="mockup-content">
              <div className="mockup-sidebar">
                <div className="sidebar-item active"></div>
                <div className="sidebar-item"></div>
                <div className="sidebar-item"></div>
                <div className="sidebar-item"></div>
              </div>
              <div className="mockup-main">
                <div className="mockup-bar"></div>
                <div className="mockup-cards">
                  <div className="mockup-card">
                    <h5>Total Items</h5>
                    <span className="mockup-card-value">1,482</span>
                    <div className="mockup-card-metric">
                      <div className="mockup-card-fill" style={{ width: "80%", background: "#55FF60" }}></div>
                    </div>
                  </div>
                  <div className="mockup-card">
                    <h5>Low Stock Alerts</h5>
                    <span className="mockup-card-value">4</span>
                    <div className="mockup-card-metric">
                      <div className="mockup-card-fill" style={{ width: "20%", background: "#ff5f56" }}></div>
                    </div>
                  </div>
                </div>
                <div className="mockup-chart-container">
                  <span style={{ fontSize: "9px", color: "rgba(255,255,255,0.4)" }}>STOCK LEVEL HISTORY</span>
                  <div className="chart-bars">
                    <div className="chart-bar" style={{ height: "45px" }}></div>
                    <div className="chart-bar" style={{ height: "55px" }}></div>
                    <div className="chart-bar highlight" style={{ height: "65px" }}></div>
                    <div className="chart-bar" style={{ height: "60px" }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="prod-features-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">INVENTORY PLATFORM</span>
          <h2 className="prod-section-title">Control Stock and Minimize Shrinkage</h2>
        </div>
        <div className="prod-features-grid">
          {features.map((feat, idx) => (
            <div className="prod-feature-card" key={idx}>
              <div className="prod-feature-icon">{feat.icon}</div>
              <h4>{feat.title}</h4>
              <p>{feat.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="prod-testimonials-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">TESTIMONIALS</span>
          <h2 className="prod-section-title">Client Success Stories</h2>
        </div>
        <div className="prod-testimonials-grid">
          <div className="prod-testimonial-card">
            <p className="prod-quote">
              "GTE Inventory streamlined our retail outlets. Stock discrepancies dropped by 95% and
              low-stock alerts save us from product shortages."
            </p>
            <div className="prod-author-info">
              <div className="prod-author-avatar">RD</div>
              <div className="prod-author-meta">
                <h5>Ramesh Deshpande</h5>
                <span>Facilities Manager, Nexus Corp</span>
              </div>
            </div>
          </div>
          <div className="prod-testimonial-card">
            <p className="prod-quote">
              "Multi-location sync helps us audit warehouse stock levels in real time. We print custom barcode
              labels directly from the system."
            </p>
            <div className="prod-author-info">
              <div className="prod-author-avatar">AR</div>
              <div className="prod-author-meta">
                <h5>Alisha Rao</h5>
                <span>Operations Head, PrimeSpaces Pvt Ltd</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="prod-faq-section">
        <div className="prod-section-header">
          <span className="prod-section-mini">FAQ</span>
          <h2 className="prod-section-title">Frequently Asked Questions</h2>
        </div>
        <div className="prod-faq-list">
          <div className="prod-faq-item">
            <h4>Does it support barcode scanner integration?</h4>
            <p>Yes, GTE Inventory integrates with standard USB and Bluetooth barcode scanners for inventory check-ins and audits.</p>
          </div>
          <div className="prod-faq-item">
            <h4>Can we set custom low-stock thresholds?</h4>
            <p>Yes, you can define custom warning thresholds for each individual product or SKU separately.</p>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <div className="con-page-wrapper">
        <ContactSection />
        <Footer />
      </div>
    </div>
  );
};

export default InventoryManagement;
